import { useState } from 'react'
import { Mic, MicOff, Upload, Loader, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { ingestAPI } from '../../lib/api'

export function VoiceInput() {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [title, setTitle] = useState('')
  const [language, setLanguage] = useState('de')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [audioURL, setAudioURL] = useState<string | null>(null)

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks: BlobPart[] = []

      recorder.ondataavailable = (e) => {
        chunks.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' })
        setAudioBlob(blob)
        setAudioURL(URL.createObjectURL(blob))
      }

      recorder.start()
      setMediaRecorder(recorder)
      setIsRecording(true)
      setError('')
    } catch (err) {
      setError('Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop()
      mediaRecorder.stream.getTracks().forEach(track => track.stop())
      setIsRecording(false)
    }
  }

  const uploadRecording = async () => {
    if (!audioBlob) return

    setIsUploading(true)
    setError('')

    try {
      // Create file from blob
      const fileName = `recording_${Date.now()}.webm`
      const audioFile = new File([audioBlob], fileName, { type: 'audio/webm' })

      // Upload via API
      await ingestAPI.uploadVoice(
        audioFile,
        title || `Sprachaufnahme ${new Date().toLocaleString('de-DE')}`,
        language
      )
      
      setSuccess(true)
      // Reset form
      setTimeout(() => {
        setTitle('')
        setAudioBlob(null)
        setAudioURL(null)
        setSuccess(false)
      }, 3000)
    } catch (err: any) {
      setError(err.message || 'Fehler beim Hochladen der Aufnahme')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold mb-2">Sprache aufnehmen</h2>
      <p className="text-gray-400 mb-8">
        Nehmen Sie gesprochene Informationen auf, die automatisch transkribiert und zur Wissensdatenbank hinzugefügt werden.
      </p>

      {/* Title Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          Titel (optional)
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z.B. Besprechung Qualitätsstandards"
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
        />
      </div>

      {/* Language Selection */}
      <div className="mb-8">
        <label className="block text-sm font-medium mb-2">
          Sprache
        </label>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
        >
          <option value="de">Deutsch</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="fr">Français</option>
          <option value="it">Italiano</option>
        </select>
      </div>

      {/* Recording Interface */}
      <div className="glass rounded-xl p-8 text-center">
        {!audioBlob ? (
          <>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={isRecording ? stopRecording : startRecording}
              className={`relative w-24 h-24 rounded-full mx-auto mb-6 transition-all ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isRecording ? (
                <>
                  <MicOff className="w-10 h-10 mx-auto text-white" />
                  <motion.div
                    className="absolute inset-0 rounded-full border-4 border-red-400"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                </>
              ) : (
                <Mic className="w-10 h-10 mx-auto text-white" />
              )}
            </motion.button>
            
            <p className="text-lg font-medium">
              {isRecording ? 'Aufnahme läuft...' : 'Klicken zum Starten'}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {isRecording 
                ? 'Klicken Sie erneut, um die Aufnahme zu beenden' 
                : 'Sprechen Sie Ihre Informationen ein'
              }
            </p>
          </>
        ) : (
          <>
            {/* Audio Preview */}
            <audio controls className="w-full mb-6" src={audioURL!}>
              Ihr Browser unterstützt keine Audio-Wiedergabe.
            </audio>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setAudioBlob(null)
                  setAudioURL(null)
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Neu aufnehmen
              </button>
              
              <button
                onClick={uploadRecording}
                disabled={isUploading}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Wird hochgeladen...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Hochladen & Transkribieren
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </motion.div>
        )}
        
        {success && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg"
          >
            <p className="text-green-500">
              ✅ Aufnahme erfolgreich hochgeladen! Die Transkription läuft im Hintergrund.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <h4 className="font-medium mb-2">💡 Hinweise zur Sprachaufnahme</h4>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Sprechen Sie deutlich und in normaler Geschwindigkeit</li>
          <li>• Vermeiden Sie Hintergrundgeräusche</li>
          <li>• Die Transkription verwendet OpenAI Whisper</li>
          <li>• Nach der Transkription wird die Extraktion automatisch gestartet</li>
        </ul>
      </div>
    </div>
  )
}